/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from '@/shared/state';
import { isTrialQuotaExhausted } from '@/shared/utils/trial-quota';
import { normalizeSearchStatus, isTerminalStatus, MAX_POLL_ATTEMPTS } from '@/shared/utils/partnerships-poll';
import { ApiError, OpenAPIHousien } from '@services/requests';
import { request as __request } from '@services/requests/core/request';
import i18next from 'i18next';
import type {
  PartnershipsWizardState,
  SearchParams,
  SearchStatus,
  PartnershipResult,
} from './partnerships-wizard-types';
import { INITIAL_WIZARD } from './partnerships-wizard-types';

export type { WizardSource, WizardStep, SearchStatus, SearchParams, PartnershipResult, PartnershipsWizardState } from './partnerships-wizard-types';

const POLL_INTERVAL_MS = 3000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollAttempts = 0;

/**
 * Maps an unknown search error into a user-facing message, distinguishing an
 * exhausted-quota response from a generic failure.
 *
 * @param err - The error thrown while starting or polling the search.
 * @returns A localized, human-readable error message.
 */
function resolveSearchError(err: unknown): string {
  const isQuotaExhausted =
    err instanceof ApiError &&
    (err.status === 402 || (err as any).body?.type === 'insufficient_partnership_searches');
  if (isQuotaExhausted) return i18next.t('features/partnerships:results.quota_exhausted');
  if (err instanceof Error) return err.message;
  return i18next.t('features/partnerships:results.search_failed');
}

type WizardSet = (fn: (s: PartnershipsWizardState) => Partial<PartnershipsWizardState>) => void;
type WizardGet = () => PartnershipsWizardState;

export interface PollResponse {
  status: SearchStatus;
  agencies_found?: number;
  properties_retrieved?: number;
  results?: PartnershipResult[];
  error?: string;
}

/**
 * Applies a completed-search poll response to the wizard state.
 *
 * @param set - The wizard store setter.
 * @param get - The wizard store getter.
 * @param data - The completed poll response payload.
 */
function applyCompleted(set: WizardSet, get: WizardGet, data: PollResponse): void {
  get().stopPolling();
  set(() => ({ results: data.results ?? [], loading: false, searched: true }));
}

/**
 * Maps a backend error code into a user-facing i18n message, so raw server
 * strings never reach the UI.
 */
export function resolveFailedError(data: PollResponse): string {
  switch (data.error) {
    case 'partnership_search_config_error':
    case 'external_portals_unreachable':
      return i18next.t('features/partnerships:results.service_unavailable');
    case 'search_timed_out':
      return i18next.t('features/partnerships:results.search_timeout');
    case 'location_discovery_failed':
      return i18next.t('features/partnerships:results.location_error');
    default:
      return i18next.t('features/partnerships:results.search_failed');
  }
}

/**
 * Applies a failed-search poll response to the wizard state.
 *
 * @param set - The wizard store setter.
 * @param get - The wizard store getter.
 * @param data - The failed poll response payload.
 */
function applyFailed(set: WizardSet, get: WizardGet, data: PollResponse): void {
  get().stopPolling();
  set(() => ({
    results: [],
    loading: false,
    error: resolveFailedError(data),
    searched: true,
  }));
}

/**
 * Applies a poll response, updating progress counters and dispatching on the
 * response's terminal status.
 *
 * @param set - The wizard store setter.
 * @param get - The wizard store getter.
 * @param data - The poll response payload.
 */
function applyPollResponse(set: WizardSet, get: WizardGet, data: PollResponse): void {
  const status = normalizeSearchStatus(data.status);
  set(() => ({
    searchStatus: status,
    agenciesFound: data.agencies_found ?? get().agenciesFound,
    propertiesRetrieved: data.properties_retrieved ?? get().propertiesRetrieved,
  }));
  if (status === 'completed') applyCompleted(set, get, data);
  else if (status === 'failed') applyFailed(set, get, data);
}

/**
 * Applies a network/poll error to the wizard state and stops polling.
 *
 * @param set - The wizard store setter.
 * @param get - The wizard store getter.
 */
function applyPollError(set: WizardSet, get: WizardGet): void {
  get().stopPolling();
  set(() => ({
    loading: false,
    error: i18next.t('features/partnerships:results.search_failed'),
    searched: true,
    searchStatus: 'failed',
  }));
}

export const {
  Context: PartnershipsWizardContext,
  Provider: PartnershipsWizardProvider,
  Consumer: PartnershipsWizardConsumer,
  useStore: usePartnershipsWizard,
} = createLocalZustand<PartnershipsWizardState>(() => (set, get) => ({
  ...INITIAL_WIZARD,
  setStep: (step) => set(() => ({ step })),
  setSource: (source) => set(() => ({ source })),
  setReferenceId: (id) => set(() => ({ referenceId: id })),
  setReferenceLabel: (label) => set(() => ({ referenceLabel: label })),
  setSeededFilters: (filters) => set(() => ({ seededFilters: filters })),
  setLastParams: (params) => set(() => ({ lastParams: params })),

  runSearch: async (params, agencyId) => {
    if (!params.city?.trim()) {
      set(() => ({
        loading: false,
        error: i18next.t('features/partnerships:results.city_required'),
        searched: false,
      }));
      return;
    }
    set(() => ({
      loading: true, error: null, trialQuotaExhausted: false, searchStatus: 'pending',
    }));
    pollAttempts = 0;
    try {
      const response = await __request(OpenAPIHousien, {
        method: 'POST',
        url: '/agencies/{agencyId}/partnerships/search',
        path: { agencyId },
        body: params,
        mediaType: 'application/json',
      });
      const data = response as { search_id: string; status: string };
      set(() => ({
        searchId: data.search_id,
        searchStatus: 'pending',
        agenciesFound: 0,
        propertiesRetrieved: 0,
      }));
      get().pollSearch(agencyId, data.search_id);
    } catch (err: unknown) {
      set(() => ({
        results: [],
        loading: false,
        error: resolveSearchError(err),
        trialQuotaExhausted: isTrialQuotaExhausted(err),
        searched: false,
        searchStatus: null,
      }));
    }
  },

  pollSearch: (agencyId, searchId) => {
    if (pollTimer) clearInterval(pollTimer);
    pollAttempts = 0;
    const poll = async () => {
      pollAttempts += 1;
      if (pollAttempts > MAX_POLL_ATTEMPTS) {
        get().stopPolling();
        set(() => ({
          loading: false,
          error: i18next.t('features/partnerships:results.search_timeout'),
          searched: true,
          searchStatus: 'failed',
        }));
        return;
      }
      try {
        const resp = await __request(OpenAPIHousien, {
          method: 'GET',
          url: '/agencies/{agencyId}/partnerships/searches/{searchId}',
          path: { agencyId, searchId },
        });
        applyPollResponse(set, get, resp as PollResponse);
      } catch {
        applyPollError(set, get);
      }
    };
    poll();
    pollTimer = setInterval(poll, POLL_INTERVAL_MS);
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    pollAttempts = 0;
  },

  reset: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    pollAttempts = 0;
    set(() => ({ ...INITIAL_WIZARD }));
  },
}));
